import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const Clients = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  // Loghi che devono essere sempre visibili (senza filtro grayscale/invert)
  const alwaysVisibleLogos = ['12.png', 'campari.png', 'tommy.png', 'souldesigner.png', 'radio-italia.png'];

  const clients = [
    { name: 'Toiletpaper', logo: 'https://www.audioxlmilano.com/assets/toilet-paper.png' },
    { name: 'Universal', logo: 'https://www.audioxlmilano.com/assets/uni.png' },
    { name: 'Petit Bateau', logo: 'https://www.audioxlmilano.com/assets/pet.png' },
    { name: 'Radio Italia', logo: 'https://www.audioxlmilano.com/assets/radio-italia.png' },
    { name: 'Sony Music', logo: 'https://www.audioxlmilano.com/assets/Sony-Music-Logo-1418x1536.png' },
    { name: 'Warner Music', logo: 'https://www.audioxlmilano.com/assets/warn.png' },
    { name: 'Tommy Hilfiger', logo: 'https://www.audioxlmilano.com/assets/tommy.png' },
    { name: 'TLF', logo: 'https://www.audioxlmilano.com/assets/tlf.png' },
    { name: 'Timberland', logo: 'https://www.audioxlmilano.com/assets/tim.png' },
    { name: 'Souldesigner', logo: 'https://www.audioxlmilano.com/assets/souldesigner.png' },
    { name: 'Comune di Milano', logo: 'https://www.audioxlmilano.com/assets/comune.png' },
    { name: 'Dolce & Gabbana', logo: 'https://www.audioxlmilano.com/assets/dg.png' },
    { name: 'Domori', logo: 'https://www.audioxlmilano.com/assets/domri.png' },
    { name: 'Eataly', logo: 'https://www.audioxlmilano.com/assets/eat.png' },
    { name: 'Foot Locker', logo: 'https://www.audioxlmilano.com/assets/foo2-1.png' },
    { name: 'InRete', logo: 'https://www.audioxlmilano.com/assets/inrete.png' },
    { name: 'Paride Vitale', logo: 'https://www.audioxlmilano.com/assets/logo-paridevitale.png' },
    { name: 'Luz', logo: 'https://www.audioxlmilano.com/assets/luz.png' },
    { name: 'Milano Wine Week', logo: 'https://www.audioxlmilano.com/assets/mila.png' },
    { name: 'Mimoto', logo: 'https://www.audioxlmilano.com/assets/mimo.png' },
    { name: '12 Floors', logo: 'https://www.audioxlmilano.com/assets/12.png' },
    { name: 'Bershka', logo: 'https://www.audioxlmilano.com/assets/ber.png' },
    { name: 'Biotek', logo: 'https://www.audioxlmilano.com/assets/bio.png' },
    { name: 'Body Heat', logo: 'https://www.audioxlmilano.com/assets/body.png' },
    { name: 'Campari', logo: 'https://www.audioxlmilano.com/assets/campari.png' },
    { name: 'Centoeventi', logo: 'https://www.audioxlmilano.com/assets/cento.png' },
    { name: 'Club Nation', logo: 'https://www.audioxlmilano.com/assets/club.png' },
    { name: 'Coca-Cola', logo: 'https://www.audioxlmilano.com/assets/cocacola.png' },
    { name: 'Comehome!', logo: 'https://www.audioxlmilano.com/assets/come.png' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.03,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  // Controlla se il logo deve essere sempre visibile
  const isAlwaysVisible = (logoUrl) => {
    return alwaysVisibleLogos.some(filename => logoUrl.includes(filename));
  };

  return (
    <section
      id="clients"
      data-testid="clients-section"
      ref={ref}
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-24 bg-[#0a0a0a]"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-20 md:mb-32"
        >
          <span className="font-heading font-bold text-sm tracking-[0.3em] text-white/40 uppercase">
            Clients
          </span>
          <h2
            className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl leading-tight mt-6"
            data-testid="clients-heading"
          >
            TRUSTED BY
            <br />
            INDUSTRY LEADERS
          </h2>
        </motion.div>

        {/* Clients Grid */}
        <motion.ul
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-4 md:gap-8"
        >
          {clients.map((client, index) => (
            <motion.li
              key={index}
              variants={itemVariants}
              className="aspect-square flex items-center justify-center p-4 md:p-6 bg-white/[0.02] border border-white/[0.05]"
              data-testid={`client-${index}`}
            >
              <img
                src={client.logo}
                alt={client.name}
                className={`w-full h-auto max-h-16 md:max-h-20 object-contain ${
                  isAlwaysVisible(client.logo) ? 'opacity-70' : 'logo-filter'
                }`}
                loading="lazy"
              />
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
};

export default Clients;
